// src/components/Sections/ContactSection.tsx
import React, { useState, useRef, useEffect } from 'react';
import {
  Mail,
  Phone,
  MapPin,
  Send,
  CheckCircle,
  Clock,
  User,
  MessageSquare,
  Building,
  Loader2,
  Users,
  AlertCircle
} from 'lucide-react';
import { useAppContext } from '../../contexts/AppContext';
import { contactService } from '../../services/api';
import ReCAPTCHA from "react-google-recaptcha";

interface Reference {
  name: string;
  project: string;
  company: string;
}

const ContactSection: React.FC = () => {
  const recaptchaKey = "6LcV0MUqAAAAACQzfzjFXbRb5gs8gEk_dOjXa3_A";
  const recaptchaRef = useRef<ReCAPTCHA>(null);
  
  const { state } = useAppContext();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    company: '',
    phone: '',
    message: ''
  });
  
  const [captchaValue, setCaptchaValue] = useState<string | null>(null);
  const [message, setMessage] = useState('');
  const [messageType, setMessageType] = useState<'success' | 'error' | ''>('');
  const [isLoading, setIsLoading] = useState(false);
  const [showCaptcha, setShowCaptcha] = useState(false);


  useEffect(() => {
    setShowCaptcha(false);
    setTimeout(() => {
      setShowCaptcha(true);
    }, 100);

  }, [state.language]);


  const contactInfo = {
    email: 'gunnar@sireus.se',
    phone: '+46 708 940567',
    location: 'Stockholm, Sverige',
    availability: true
  };

  const references: Reference[] = [
    {
      name: 'Referenser lämnas på begäran',
      project: state.language === 'sv'
        ? 'Schemaläggningsapp för ambulanspersonal'
        : 'Scheduling app for ambulance personnel',
      company: 'AdviceTech'
    },
    {
      name: 'Referenser lämnas på begäran',
      project: state.language === 'sv'
        ? 'Flödesbaserade kapacitetsberäkningar'
        : 'Flow-based capacity calculations',
      company: 'Svenska Kraftnät'
    }
  ];

  // Translation helper
  const t = (sv: string, en: string) => state.language === 'sv' ? sv : en;

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleCaptchaChange = (value: string | null) => {
    setCaptchaValue(value);
  };

  const validateForm = () => {
    if (!formData.name || !formData.email || !formData.message) {
      setMessage(t('Alla obligatoriska fält måste fyllas i.', 'All required fields must be filled.'));
      setMessageType('error');
      return false;
    }
    
    const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailPattern.test(formData.email)) {
      setMessage(t('Vänligen ange en giltig e-postadress.', 'Please enter a valid email address.'));
      setMessageType('error');
      return false;
    }
    
    if (formData.message.length < 10) {
      setMessage(t('Meddelandet måste vara minst 10 tecken.', 'Message must be at least 10 characters.'));
      setMessageType('error');
      return false;
    }
    
    if (!captchaValue) {
      setMessage(t('Vänligen bekräfta CAPTCHA.', 'Please complete the CAPTCHA.'));
      setMessageType('error');
      return false;
    }
    
    return true;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validateForm()) return;

    setIsLoading(true);
    setMessage('');
    setMessageType('');

    try {
      await contactService.sendContactForm({
        name: formData.name,
        email: formData.email,
        company: formData.company || undefined,
        phone: formData.phone || undefined,
        message: formData.message,
        captchaToken: captchaValue!,
        language: state.language
      });

      setMessage(t('Ditt meddelande har skickats!', 'Your message has been sent successfully!'));
      setMessageType('success');
      
      // Reset form
      setFormData({ name: '', email: '', company: '', phone: '', message: '' });
      setCaptchaValue(null);
      
      // Reset reCAPTCHA
      if (recaptchaRef.current) {
        recaptchaRef.current.reset();
      }
      
      // Clear success message after 5 seconds
      setTimeout(() => {
        setMessage('');
        setMessageType('');
      }, 5000);
      
    } catch (error) {
      setMessage(
        error instanceof Error 
          ? error.message 
          : t('Något gick fel. Försök igen.', 'Something went wrong. Please try again.')
      );
      setMessageType('error');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <section id="contact" className="py-20 bg-gray-50 dark:bg-gray-800">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-16">
          <div className="inline-flex items-center px-4 py-2 bg-blue-100 dark:bg-blue-900/30 rounded-full mb-6">
            <span className="text-sm font-medium text-blue-600 dark:text-blue-400">
              {t('Kontakt', 'Contact')}
            </span>
          </div>

          <h2 className="text-4xl font-bold text-gray-900 dark:text-white mb-4">
            {t('Låt oss diskutera ditt nästa projekt', "Let's discuss your next project")}
          </h2>

          <p className="text-xl text-gray-600 dark:text-gray-400 max-w-3xl mx-auto">
            {t(
              'Har du ett projekt som behöver senior expertis? Jag finns här för att hjälpa.',
              'Do you have a project that needs senior expertise? I\'m here to help.'
            )}
          </p>
        </div>

        <div className="grid lg:grid-cols-3 gap-12">
          {/* Contact Form */}
          <div className="lg:col-span-2">
            <div className="bg-white dark:bg-gray-900 rounded-xl shadow-lg p-8">
              
              {/* Message Display */}
              {message && (
                <div className={`mb-6 p-4 rounded-lg ${
                  messageType === 'success' 
                    ? 'bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800' 
                    : 'bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800'
                }`}>
                  <div className="flex items-start">
                    {messageType === 'success' ? (
                      <CheckCircle className="h-5 w-5 text-green-600 dark:text-green-400 mt-0.5 mr-3" />
                    ) : (
                      <AlertCircle className="h-5 w-5 text-red-600 dark:text-red-400 mt-0.5 mr-3" />
                    )}
                    <p className={`text-sm ${
                      messageType === 'success'
                        ? 'text-green-800 dark:text-green-200'
                        : 'text-red-800 dark:text-red-200'
                    }`}>
                      {message}
                    </p>
                  </div>
                </div>
              )}

              <form onSubmit={handleSubmit}>
                <div className="grid md:grid-cols-2 gap-6 mb-6">
                  <div>
                    <label htmlFor="name" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      <User className="inline h-4 w-4 mr-2" />
                      {t('Namn', 'Name')} *
                    </label>
                    <input
                      type="text"
                      id="name"
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      required
                      className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white dark:bg-gray-800 text-gray-900 dark:text-white"
                      placeholder={t('Ditt namn', 'Your name')}
                    />
                  </div>

                  <div>
                    <label htmlFor="email" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      <Mail className="inline h-4 w-4 mr-2" />
                      {t('E-post', 'Email')} *
                    </label>
                    <input
                      type="email"
                      id="email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      required
                      className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white dark:bg-gray-800 text-gray-900 dark:text-white"
                      placeholder={t('din@email.se', 'your@email.com')}
                    />
                  </div>
                </div>

                <div className="grid md:grid-cols-2 gap-6 mb-6">
                  <div>
                    <label htmlFor="company" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      <Building className="inline h-4 w-4 mr-2" />
                      {t('Företag', 'Company')}
                    </label>
                    <input
                      type="text"
                      id="company"
                      name="company"
                      value={formData.company}
                      onChange={handleChange}
                      className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white dark:bg-gray-800 text-gray-900 dark:text-white"
                      placeholder={t('Ditt företag', 'Your company')}
                    />
                  </div>

                  <div>
                    <label htmlFor="phone" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      <Phone className="inline h-4 w-4 mr-2" />
                      {t('Telefon', 'Phone')}
                    </label>
                    <input
                      type="tel"
                      id="phone"
                      name="phone"
                      value={formData.phone}
                      onChange={handleChange}
                      className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white dark:bg-gray-800 text-gray-900 dark:text-white"
                      placeholder="+46 70 123 45 67"
                    />
                  </div>
                </div>

                <div className="mb-6">
                  <label htmlFor="message" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    <MessageSquare className="inline h-4 w-4 mr-2" />
                    {t('Meddelande', 'Message')} *
                  </label>
                  <textarea
                    id="message"
                    name="message"
                    rows={5}
                    value={formData.message}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white dark:bg-gray-800 text-gray-900 dark:text-white"
                    placeholder={t('Berätta om ditt projekt...', 'Tell me about your project...')}
                  />
                </div>

                <div className="mb-6">
                  {showCaptcha &&
                    <ReCAPTCHA
                      ref={recaptchaRef}
                      sitekey={recaptchaKey}
                      size='normal'
                      badge='inline'
                      onChange={handleCaptchaChange}
                      theme={state.theme === 'dark' ? 'dark' : 'light'}
                      hl={state.language === 'sv' ? 'sv' : 'en'}
                    />}
                </div>

                <div className="text-sm text-gray-500 dark:text-gray-400 mb-6">
                  * {t('Obligatoriska fält', 'Required fields')}
                </div>

                <button
                  type="submit"
                  disabled={isLoading || !captchaValue}
                  className="w-full px-8 py-4 bg-blue-600 hover:bg-blue-700 disabled:bg-blue-400 disabled:cursor-not-allowed text-white font-medium rounded-lg transition-colors duration-200 flex items-center justify-center"
                >
                  {isLoading ? (
                    <>
                      <Loader2 className="animate-spin h-5 w-5 mr-2" />
                      {t('Skickar...', 'Sending...')}
                    </>
                  ) : (
                    <>
                      <Send className="h-5 w-5 mr-2" />
                      {t('Skicka meddelande', 'Send message')}
                    </>
                  )}
                </button>
              </form>
            </div>
          </div>

          {/* Contact Information */}
          <div className="space-y-6">
            {/* Direct Contact */}
            <div className="bg-white dark:bg-gray-900 rounded-xl shadow-lg p-6">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-6">
                {t('Kontaktuppgifter', 'Contact Information')}
              </h3>

              <div className="space-y-4">
                <a
                  href={`mailto:${contactInfo.email}`}
                  className="flex items-start hover:text-blue-600 dark:hover:text-blue-400 transition-colors"
                >
                  <Mail className="h-5 w-5 text-blue-600 dark:text-blue-400 mr-3 mt-0.5" />
                  <div>
                    <p className="text-sm text-gray-500 dark:text-gray-400 mb-1">
                      {t('E-post', 'Email')}
                    </p>
                    <p className="text-gray-900 dark:text-white font-medium">
                      {contactInfo.email}
                    </p>
                  </div>
                </a>

                <a
                  href={`tel:${contactInfo.phone.replace(/\s/g, '')}`}
                  className="flex items-start hover:text-blue-600 dark:hover:text-blue-400 transition-colors"
                >
                  <Phone className="h-5 w-5 text-blue-600 dark:text-blue-400 mr-3 mt-0.5" />
                  <div>
                    <p className="text-sm text-gray-500 dark:text-gray-400 mb-1">
                      {t('Telefon', 'Phone')}
                    </p>
                    <p className="text-gray-900 dark:text-white font-medium">
                      {contactInfo.phone}
                    </p>
                  </div>
                </a>

                <div className="flex items-start">
                  <MapPin className="h-5 w-5 text-blue-600 dark:text-blue-400 mr-3 mt-0.5" />
                  <div>
                    <p className="text-sm text-gray-500 dark:text-gray-400 mb-1">
                      {t('Plats', 'Location')}
                    </p>
                    <p className="text-gray-900 dark:text-white font-medium">
                      {state.language === 'sv' ? 'Stockholm, Sverige' : 'Stockholm, Sweden'}
                    </p>
                  </div>
                </div>

                <div className="flex items-start">
                  <Clock className="h-5 w-5 text-blue-600 dark:text-blue-400 mr-3 mt-0.5" />
                  <div>
                    <p className="text-sm text-gray-500 dark:text-gray-400 mb-1">
                      {t('Tillgänglighet', 'Availability')}
                    </p>
                    <p className="text-gray-900 dark:text-white font-medium">
                      {contactInfo.availability ? (
                        <span className="inline-flex items-center">
                          <span className="w-2 h-2 bg-green-500 rounded-full mr-2"></span>
                          {t('Tillgänglig för uppdrag', 'Available for assignments')}
                        </span>
                      ) : (
                        <span className="inline-flex items-center">
                          <span className="w-2 h-2 bg-red-500 rounded-full mr-2"></span>
                          {t('Fullbokad just nu', 'Fully booked right now')}
                        </span>
                      )}
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* References */}
            <div className="bg-white dark:bg-gray-900 rounded-xl shadow-lg p-6">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-6 flex items-center">
                <Users className="h-5 w-5 mr-2 text-blue-600 dark:text-blue-400" />
                {t('Referenser', 'References')}
              </h3>

              <div className="space-y-4">
                {references.map((ref, index) => (
                  <div key={index} className="border-l-2 border-blue-200 dark:border-blue-800 pl-4">
                    <p className="font-medium text-gray-900 dark:text-white">
                      {ref.name}
                    </p>
                    <p className="text-sm text-gray-600 dark:text-gray-400">
                      {ref.company}
                    </p>
                    <p className="text-xs text-gray-500 dark:text-gray-500 mt-1">
                      {ref.project}
                    </p>
                  </div>
                ))}
              </div>

              <p className="text-sm text-gray-500 dark:text-gray-400 mt-4 italic">
                {t('Fullständiga kontaktuppgifter lämnas på förfrågan', 'Full contact details available upon request')}
              </p>
            </div>

            {/* Quick Info */}
            <div className="bg-gradient-to-br from-blue-50 to-indigo-50 dark:from-blue-900/20 dark:to-indigo-900/20 rounded-xl p-6">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-3">
                {t('Snabbfakta', 'Quick Facts')}
              </h3>
              <ul className="space-y-2 text-sm text-gray-600 dark:text-gray-400">
                <li className="flex items-center">
                  <CheckCircle className="h-4 w-4 text-green-500 mr-2" />
                  {t('25+ års erfarenhet', '25+ years of experience')}
                </li>
                <li className="flex items-center">
                  <CheckCircle className="h-4 w-4 text-green-500 mr-2" />
                  {t('Baserad i Stockholm', 'Based in Stockholm')}
                </li>
                <li className="flex items-center">
                  <CheckCircle className="h-4 w-4 text-green-500 mr-2" />
                  {t('Arbetar gärna remote', 'Happy to work remotely')}
                </li>
                <li className="flex items-center">
                  <CheckCircle className="h-4 w-4 text-green-500 mr-2" />
                  {t('Siréus Consulting AB', 'Siréus Consulting AB')}
                </li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ContactSection;